import { POSITION_CODES } from '../data/positions.ts';
import type { DepthOrder, Player, PositionCode } from '../types.ts';

/**
 * The positions the depth chart keeps a row for. The middle centre-back of a back three
 * is the same job as either flanked centre-back, so it does not get a row of its own.
 */
export const DEPTH_POSITIONS: PositionCode[] = POSITION_CODES.filter((code) => code !== 'CB');

/** The row a player falls into when nobody has moved him. */
function homeRow(code: PositionCode): PositionCode {
  return code === 'CB' ? 'LCB' : code;
}

/**
 * Turns a possibly stale, possibly absent override into a complete chart.
 *
 * Every player lands in exactly one row. Ids the override no longer knows about — a
 * signing from the transfer simulator, or the whole squad when there is no override at
 * all — fall in under their dataset position, strongest rating first. Ids that no longer
 * belong to anyone are dropped. That keeps a saved chart usable after the squad changes
 * underneath it, instead of quietly losing players.
 */
export function resolveDepthOrder(players: Player[], override: DepthOrder | null): DepthOrder {
  const known = new Set(players.map((p) => p.id));
  const resolved: DepthOrder = {};
  for (const code of DEPTH_POSITIONS) resolved[code] = [];

  const placed = new Set<string>();
  if (override) {
    for (const code of DEPTH_POSITIONS) {
      for (const id of override[code] ?? []) {
        if (known.has(id) && !placed.has(id)) {
          resolved[code]!.push(id);
          placed.add(id);
        }
      }
    }
  }

  players
    .filter((p) => !placed.has(p.id))
    .sort((a, b) => b.rating - a.rating)
    .forEach((p) => resolved[homeRow(p.positions[0])]!.push(p.id));

  return resolved;
}

/**
 * Rewrites each player's primary position to the row the user filed him under, keeping
 * his remaining dataset positions behind it. This is what makes the chart more than a
 * picture: the auto-lineup, the poster and the squad table all read `positions[0]`, so
 * moving a chip actually moves the player.
 *
 * The dataset itself is never touched — this is a projection over it. `positionWeights`
 * is deliberately left alone: filing a player under a row says where he is being used,
 * not what he is worth there, so `suitability` keeps reporting his real weight and the
 * pitch still marks him as covering rather than at home.
 */
export function applyDepthPositions(players: Player[], resolved: DepthOrder): Player[] {
  const rowOf = new Map<string, PositionCode>();
  for (const code of DEPTH_POSITIONS) {
    for (const id of resolved[code] ?? []) rowOf.set(id, code);
  }
  return players.map((p) => {
    const code = rowOf.get(p.id);
    if (!code || p.positions[0] === code) return p;
    return { ...p, positions: [code, ...p.positions.filter((c) => c !== code)] };
  });
}

/**
 * Moves a player to `position`, landing immediately before `beforeId` or at the end when
 * that is null.
 *
 * Deliberately expressed as "before this player" rather than "at index N": the player is
 * pulled out of his old row first, which shifts every index after him, and a numeric
 * index would then be off by one whenever someone is dragged further down his own row.
 */
export function moveInDepth(
  resolved: DepthOrder,
  id: string,
  position: PositionCode,
  beforeId: string | null,
): DepthOrder {
  const next: DepthOrder = {};
  for (const code of DEPTH_POSITIONS) {
    next[code] = (resolved[code] ?? []).filter((x) => x !== id);
  }
  const target = next[position]!;
  const at = beforeId ? target.indexOf(beforeId) : -1;
  if (at < 0) target.push(id);
  else target.splice(at, 0, id);
  return next;
}
